import React, {useCallback} from 'react'

import {useProductList} from '../util/product-list-context'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import useEinstein from '../../../hooks/use-einstein'
import {Box, SimpleGrid} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import PageHeader from './page-header'
import ProductListTopPanel from './product-list-top-panel'
import RefinementsDrawer from './refinements-drawer'

import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'

// Custom Components
import MetaTagsComponent from '../../../components/metatags'

// Custom utils
import {buildMetaData} from '../../../components/metatags/util/metaDataUtils'

/**
 * A React functional component that renders a pattern product list page layout.
 *
 * @returns {JSX.Element} A JSX element representing the product list page layout.
 */
const ProductListPattern = () => {
    const {
        category,
        isRefetching,
        isFetched,
        productSearchResult,
        searchParams,
        searchQuery,
        metatags
    } = useProductList()
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const einstein = useEinstein()

    /*
     * Metadata constructed in the getProps method and passed to the MetaTags component.
     */
    const finalMetadata = buildMetaData(category, metatags, searchQuery)

    const memoizedHandleProductClick = useCallback((productSearchItem) => {
        if (searchQuery) {
            einstein.sendClickSearch(searchQuery, productSearchItem)
        } else if (category) {
            einstein.sendClickCategory(category, productSearchItem)
        }
    }, [])

    return (
        <>
            <MetaTagsComponent metaData={finalMetadata} />
            {/* Header */}
            <PageHeader />
            <Box as="section">
                {/* Top PLP panel */}
                <ProductListTopPanel isLinkedCategories={false} />

                {/* PLP Body  */}
                {/* @TODO: it's playground to test gender filter and should be re-worked on Pattern PLP task */}
                <SimpleGrid
                    columns={[2, 2, 3, 4, 4, 5]}
                    spacingX={10}
                    spacingY={{base: 24, lg: 70}}
                    borderTopWidth={1}
                    borderColor="borderGray"
                >
                    {isHydrated() && ((isRefetching && !isFetched) || !productSearchResult)
                        ? new Array(searchParams.limit)
                              .fill(0)
                              .map((value, index) => (
                                  <ProductTileSkeleton key={`product-tile-skeleton-${index}`} />
                              ))
                        : productSearchResult?.hits?.map((productSearchItem, index) => {
                              const productId = productSearchItem.productId
                              const isInWishlist = !!wishlist?.customerProductListItems?.find(
                                  (item) => item.productId === productId
                              )

                              return (
                                  <ProductTile
                                      data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                      key={productSearchItem.productId}
                                      product={productSearchItem}
                                      isFavourite={isInWishlist}
                                      isFavIconInTitle={true}
                                      isRefreshingData={isRefetching && isFetched}
                                      onClick={() => memoizedHandleProductClick(productSearchItem)}
                                      onFavouriteToggle={(toBeFavourite) => {
                                          const action = toBeFavourite
                                              ? addProductToWishlist
                                              : removeProductFromWishlist
                                          return action(productSearchItem)
                                      }}
                                      dynamicImageProps={{
                                          widths: ['50vw', '50vw', '33vw', '25vw', '25vw', '20vw']
                                      }}
                                      isImageLazyLoading={index > 3}
                                  />
                              )
                          })}
                </SimpleGrid>

                {/* Drawer for filter options */}
                <RefinementsDrawer />
            </Box>
        </>
    )
}

export default ProductListPattern
